import { Component } from '@theme/component';
import EmblaCarousel, {
  EmblaCarouselType,
  EmblaOptionsType,
} from 'embla-carousel';
import AutoScroll from 'embla-carousel-auto-scroll';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

const MOBILE_MEDIA = '(max-width: 749px)';

function preloadImage(img: HTMLImageElement) {
  if (img.dataset.preloaded === 'true') return;
  if (img.complete && img.naturalWidth > 0) {
    img.dataset.preloaded = 'true';
    return;
  }

  img.dataset.preloaded = 'true';
  const wasLazy = img.getAttribute('loading') === 'lazy';
  img.loading = 'eager';
  if (!wasLazy || img.currentSrc) {
    img.decode?.().catch(() => {});
    return;
  }

  const srcset = img.getAttribute('srcset');
  const src = img.getAttribute('src');
  if (srcset) img.srcset = srcset;
  if (src) img.src = src;

  if (srcset || src) {
    const preloader = new Image();
    if (img.sizes) preloader.sizes = img.sizes;
    if (srcset) preloader.srcset = srcset;
    if (src) preloader.src = src;
  }

  img.decode?.().catch(() => {});
}

function preloadSlide(slide: HTMLElement | undefined) {
  if (!slide) return;

  slide.querySelectorAll('img').forEach((img) => {
    if (img instanceof HTMLImageElement) preloadImage(img);
  });

  slide.querySelectorAll('[poster]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const poster = el.getAttribute('poster');
    if (!poster || el.dataset.posterPreloaded === 'true') return;
    el.dataset.posterPreloaded = 'true';
    const preloader = new Image();
    preloader.src = poster;
  });

  slide.querySelectorAll('iframe').forEach((iframe) => {
    if (iframe instanceof HTMLIFrameElement) iframe.loading = 'eager';
  });
}

class RhmCarouselComponent extends Component {
  #emblaApi: EmblaCarouselType | null = null;
  #dotsAbort: AbortController | null = null;
  #preloadAbort: AbortController | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dotsAbort?.abort();
    this.#dotsAbort = null;
    this.#preloadAbort?.abort();
    this.#preloadAbort = null;
    this.#emblaApi?.destroy();
    this.#emblaApi = null;
  }

  scrollPrev() {
    this.#emblaApi?.scrollPrev();
  }

  scrollNext() {
    this.#emblaApi?.scrollNext();
  }

  #init() {
    const viewport = this.refs.viewport as HTMLElement | undefined;
    if (!viewport) return;

    const dragFree = this.dataset.dragFree !== 'false';
    const loop = this.dataset.loop === 'true';
    const slidesToScroll = this.dataset.slidesToScroll;
    const alignAttr = this.dataset.align;
    const align =
      alignAttr === 'center' || alignAttr === 'end' || alignAttr === 'start'
        ? alignAttr
        : 'start';

    const containScrollAttr = this.dataset.containScroll;
    let containScroll: EmblaOptionsType['containScroll'] = 'trimSnaps';
    if (containScrollAttr === 'false') {
      containScroll = false;
    } else if (containScrollAttr === 'keepSnaps') {
      containScroll = 'keepSnaps';
    }

    const options: EmblaOptionsType = {
      align,
      dragFree,
      loop,
      containScroll,
    };

    if (slidesToScroll === 'auto') {
      options.slidesToScroll = 'auto';
    } else if (slidesToScroll) {
      const parsed = parseInt(slidesToScroll, 10);
      if (!Number.isNaN(parsed) && parsed > 0) {
        options.slidesToScroll = parsed;
      }
    }

    const plugins = [WheelGesturesPlugin()];

    if (this.dataset.autoScroll === 'true') {
      const speed = parseFloat(this.dataset.autoScrollSpeed || '2');
      plugins.push(
        AutoScroll({
          speed,
          stopOnInteraction: false,
          stopOnMouseEnter: true,
          playOnInit: true,
        }) as any
      );
    }

    this.#emblaApi = EmblaCarousel(viewport, options, plugins as any);

    const onSelect = () => {
      this.#updateButtons();
      this.#updateSnapDisplay();
      this.#updateDots();
      this.#preloadUpcoming();
    };

    this.#emblaApi.on('select', onSelect).on('reInit', onSelect).on('slidesInView', () => {
      this.#preloadUpcoming();
    });
    onSelect();

    if ('preloadNext' in this.dataset) {
      this.#preloadAbort?.abort();
      const preloadAbort = new AbortController();
      this.#preloadAbort = preloadAbort;
      window.matchMedia(MOBILE_MEDIA).addEventListener('change', () => this.#preloadUpcoming(), {
        signal: preloadAbort.signal,
      });
    }
  }

  /**
   * Full-bleed mobile slides sit outside the clip, so lazy images stay blank
   * until the swipe reveals them. Opt in with `data-preload-next`.
   */
  #preloadUpcoming() {
    if (!('preloadNext' in this.dataset) || !this.#emblaApi) return;
    if (!window.matchMedia(MOBILE_MEDIA).matches) return;

    const slides = this.#emblaApi.slideNodes();
    const selected = this.#emblaApi.selectedScrollSnap();
    const inView = this.#emblaApi.slidesInView();
    let furthest = selected;
    inView.forEach((index) => {
      if (index > furthest) furthest = index;
    });

    preloadSlide(slides[selected]);
    preloadSlide(slides[furthest + 1]);
  }

  #updateButtons() {
    if (!this.#emblaApi) return;
    const prevBtn = this.refs.prevBtn as HTMLButtonElement | undefined;
    const nextBtn = this.refs.nextBtn as HTMLButtonElement | undefined;
    const arrows = this.querySelector('.rhm-carousel__arrows') as HTMLElement | null;
    const canScroll = this.#emblaApi.scrollSnapList().length > 1;

    if (arrows) {
      arrows.hidden = !canScroll;
    }

    if (prevBtn) prevBtn.disabled = false;
    if (nextBtn) nextBtn.disabled = false;
  }

  #updateSnapDisplay() {
    if (!this.#emblaApi) return;
    const snapDisplay = this.refs.snapDisplay as HTMLElement | undefined;
    if (!snapDisplay) return;
    const current = this.#emblaApi.selectedScrollSnap() + 1;
    const total = this.#emblaApi.scrollSnapList().length;
    snapDisplay.textContent = `${current} / ${total}`;
  }

  #updateDots() {
    if (!this.#emblaApi) return;
    const dotsContainer = this.refs.dots as HTMLElement | undefined;
    if (!dotsContainer) return;

    this.#dotsAbort?.abort();
    this.#dotsAbort = new AbortController();
    const { signal } = this.#dotsAbort;

    const snaps = this.#emblaApi.scrollSnapList();
    const selected = this.#emblaApi.selectedScrollSnap();
    const pageLabelTemplate =
      dotsContainer.dataset.pageLabel || 'Go to page [PAGE]';

    dotsContainer.replaceChildren();

    if (snaps.length <= 1) {
      dotsContainer.hidden = true;
      return;
    }

    dotsContainer.hidden = false;
    dotsContainer.setAttribute('role', 'tablist');

    const tablistLabel = dotsContainer.dataset.tablistLabel;
    if (tablistLabel) {
      dotsContainer.setAttribute('aria-label', tablistLabel);
    }

    snaps.forEach((_, index) => {
      const dot = document.createElement('button');
      const pageNumber = String(index + 1);

      dot.type = 'button';
      dot.className = 'rhm-carousel__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute(
        'aria-label',
        pageLabelTemplate.replace('[PAGE]', pageNumber)
      );
      dot.setAttribute('aria-selected', index === selected ? 'true' : 'false');

      if (index === selected) {
        dot.classList.add('is-selected');
      }

      dot.addEventListener(
        'click',
        () => {
          this.#emblaApi?.scrollTo(index);
        },
        { signal }
      );

      dotsContainer.appendChild(dot);
    });
  }
}

if (!customElements.get('rhm-carousel-component')) {
  customElements.define('rhm-carousel-component', RhmCarouselComponent);
}
